import {
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Theme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Formik } from 'formik';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { useRecoilStateLoadable, useRecoilValueLoadable } from 'recoil';
import { MaterialAutocomplete } from '../../../common/formik-material-ui';
import { IUserAtom, user } from '../../../recoil/atoms/user';
import {
  makePHPRequest,
  methodNameEnum,
} from '../../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import { endpointEnum } from '../../../services/rest/makeAPIRequest';
import { useSnackbarWithStyles } from '../../components/notifications/SnackbarProviderWithStyles';

import { useHistory } from 'react-router-dom';

const useStyles = makeStyles((theme: Theme) => ({
  fullWidth: {
    width: '100% !important',
  },
  actionColumn: {
    display: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
  numbersList: {
    padding: '0',
    paddingBottom: '1px',
    listStyleType: 'none',
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  button: {
    marginLeft: theme.spacing(1), // space between fields
  },
  labels: {
    marginBottom: 0,
    paddingBottom: 0,
    fontSize: '12px',
  },
  formContainer: {
    marginTop: '20px',
    marginBottom: '30px',
    width: '60%',
  },
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
}));

export const QueueSlideForm = (props: Record<any, any>) => {
  const [autocomplete, setAutocomplete] = useState<any>({
    queuename: 'ab test queue',
    queueid: 32,
  });
  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable<
    IUserAtom | Record<string, any>
  >(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  const mutation = useMutation(getApiMutation(userAtomContents, setUserAtom));
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const history = useHistory();

  const businessQueuesLoadable = useRecoilValueLoadable(
    makePHPRequest({ methodName: methodNameEnum.GET_BUSINESS_QUEUES })
  );
  // const setRefetchToggleForBusinessQueues = useSetRecoilState(
  //   refetchToggle(methodNameEnum.GET_BUSINESS_QUEUES)
  // );

  /**
   * Transform by adding id
   * NOTE: must use useMemo so rows do not get updated on re-render
   */
  const queuesCached = useRef<any[]>([]);
  queuesCached.current = React.useMemo(
    () =>
      businessQueuesLoadable.state !== 'loading'
        ? businessQueuesLoadable.contents?.data?.result
            ?.map((row: any) => ({
              ...row,
              id: +row.queueid, // allow number sorting
            }))
            ?.sort((a: Queue, b: Queue) =>
              a?.queuename
                ?.toLowerCase()
                ?.localeCompare(b?.queuename?.toLowerCase())
            )
        : queuesCached.current,
    [businessQueuesLoadable]
  );
  // console.log('queues cached: ', queuesCached);

  type Queue = {
    queueid: string;
    queuename: string;
    active: string;
    created: string;
    updated: string;
    businessid: string;
    email: string;
    defaultuser: string;
    greeting: string;
    ringmusic: string;
    defaultAction: string;
    queueType: string;
    maxduration: string;
    matchState: string;
    ringtimeout: string;
    vmGreeting: string;
    playWaitTime: string;
    inQueueIVR: string;
    inQueueVoicemail: string;
    inQueueCallback: string;
    autoAnswerInbound: string;
    autoAnswerReturnedCall: string;
    DndAfterMissedCall: string;
    subscribeOnLogin: string;
    assignOwnerOnAnswer: string;
    overrideOwnerOnAnswer: string;
    initialAttempt: string;
    closedAction: string;
    queueHours: string;
    useBusinessHoursEnabled: string;
    isAgentQueue: string;
    hasGreeting: string;
    hasRingMusic: string;
    id: number;
  };

  const initialValues = useMemo<any>(() => {
    const slide = props?.props?.data?.[props.props.openIndex] ?? {};
    return {
      queueDropdown: slide?.queueid ?? '',
      coverTime: slide?.timePeriod ?? '',
      metric: slide?.metric ?? '',
      slideTime: slide?.slideDurationInSec ?? '',
    };
  }, [props.props.id, businessQueuesLoadable, autocomplete]);
  useEffect(() => {
    const slide = props?.props?.data?.[props.props.openIndex] ?? {};

    setAutocomplete(slide?.queueid);
  }, [businessQueuesLoadable, queuesCached, props.props.id]);

  const classes = useStyles();
  return (
    <Formik
      enableReinitialize={true}
      onSubmit={async (values, actions) => {
        const updatedSlide = props.props.data[props.props.openIndex];
        const newSlide = {
          ...updatedSlide,
          queueid: values.queueDropdown,
          slideDurationInSec: values.coverTime,
          id: values.queueDropdown.queueid,
        };
        const upsertSlidesArr = [...props.props.data];
        upsertSlidesArr[props.props.openIndex] = newSlide;

        const payload = {
          businessid: userAtomContents?.businessid,
          leaderboardId:
            props.props.leaderboardId === 'new'
              ? null
              : props.props.leaderboardId,
          leaderboardName:
            props.props.leaderboardId === 'new'
              ? `Leaderboard ${props.props.numbLeaderboards + 1}`
              : props.props.leaderboardName,
          createdById: updatedSlide.createdById ?? userAtomContents.userid,
          slides: JSON.stringify(upsertSlidesArr),
        };

        const upsertRes = await mutation.mutateAsync({
          domain: process.env.VITE_APP_CDK_DOMAIN,
          endpoint: endpointEnum.UPSERT_LEADERBOARD,
          method: 'post',
          payload,
        });
        if (upsertRes?.data?.success) {
          props.props.setRefetchToggleForLeaderboards({});
          enqueueSnackbarWithStyles({
            message: `Successfully updated slide`,
            alertProps: { severity: 'success' },
          });
          if (props.props.leaderboardId === 'new') {
            const pathArr = window.location.pathname.split('/');
            pathArr[pathArr.length - 1] = upsertRes.data?.data?.leaderboardId;
            history.push(pathArr.join('/'));
          }
        } else {
          enqueueSnackbarWithStyles({
            message: `Something went wrong, slide failed to update`,
            alertProps: { severity: 'error' },
          });
        }
        props.props.resetStates();
      }}
      initialValues={initialValues}
    >
      {(formik) => {
        return (
          <form>
            <div className={`${classes.formContainer} container`}>
              <p className={classes.labels}>
                Select the queue(s) this slide should display
              </p>
              <div className="row">
                <div className="col-lg-8">
                  <MaterialAutocomplete
                    // defaultValue={{
                    //   lastUpdated: '',
                    //   memberCount: 0,
                    //   teamid: 0,
                    //   teamId: 0,
                    //   teamname: 'All Teams',
                    // }}
                    // defaultValue={queuesCached.current?.[5]}
                    style={{ width: '400px' }}
                    className={`flex-grow-1 ${classes.field}`}
                    type="text"
                    label="Queues"
                    size="small"
                    name="queueDropdown"
                    variant="outlined"
                    // options={teamsList.current ?? []}
                    options={queuesCached.current ?? []}
                    value={autocomplete}
                    multiple={false}
                    onChange={(e, newValue: Queue) => {
                      setAutocomplete({
                        queuename: newValue?.queuename,
                        queueid: newValue?.id,
                      });
                      formik.setFieldValue('queueDropdown', {
                        queuename: newValue?.queuename,
                        queueid: newValue?.id,
                      });
                    }}
                    getOptionLabel={(option) => option.queuename ?? ''}
                    isOptionEqualToValue={(option, value) => {
                      return option?.id === value?.queueid;
                    }}
                    disabled={businessQueuesLoadable.state === 'loading'} // in progress or queue loading
                  />
                </div>
              </div>
              <FormControl>
                <p className={classes.labels} style={{ marginTop: '20px' }}>
                  How long should this slide display?
                </p>
                <RadioGroup
                  aria-labelledby="radio-buttons-group-queue-slide-time"
                  name="slideTime"
                  style={{ marginLeft: '13px' }}
                  defaultValue={initialValues?.slideTime ?? ''}
                  onChange={(e) => {
                    formik.setFieldValue('coverTime', (e.target as any).value);
                  }}
                >
                  <div className="row" style={{ width: '175%' }}>
                    <div style={{ width: '50%' }}>
                      <FormControlLabel
                        value="15seconds"
                        control={<Radio color="primary" />}
                        label="15 seconds"
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <FormControlLabel
                        value="30seconds"
                        control={<Radio color="primary" />}
                        label="30 seconds"
                      />
                    </div>
                  </div>
                  <div className="row" style={{ width: '175%' }}>
                    <div style={{ width: '50%' }}>
                      <FormControlLabel
                        value="1minute"
                        control={<Radio color="primary" />}
                        label="1 minute"
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <FormControlLabel
                        value="5minutes"
                        control={<Radio color="primary" />}
                        label="5 minutes"
                      />
                    </div>
                  </div>
                </RadioGroup>
                <div className="row">
                  <Button
                    variant="contained"
                    disableElevation={true}
                    className={classes.button}
                    onClick={() => {
                      props.props.resetStatesOnCancel();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    disableElevation={true}
                    disabled={
                      !formik.dirty ||
                      formik.values.coverTime === '' ||
                      !formik.values.queueDropdown?.queuename
                    }
                    color="primary"
                    onClick={() => {
                      formik.handleSubmit();
                    }}
                    className={classes.button}
                  >
                    Save
                  </Button>
                </div>
              </FormControl>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};
